import _ from 'lodash'
import React, { Component } from 'react'

import apis from 'browser/app/models/apis'
import { ErrorBlock } from 'browser/components/atomic-elements/atoms/error-block/error-block'
import { Footer } from 'browser/components/atomic-elements/atoms/footer/footer'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import { Position, Toast } from 'browser/components/atomic-elements/atoms/toast/toast'
import { InputField } from 'browser/components/atomic-elements/molecules/fields/input-field/input-field'
import OverlayManager from 'browser/components/atomic-elements/organisms/overlay-manager/overlay-manager'
import { stringifyError } from 'shared-libs/models/utils'

const DEFAULT_DURATION_DAYS = 3650

interface IIssueTokenDialogProps {
  onClose: () => void
  targetUserId: string
}

interface IIssueTokenDialogState {
  isLoading: boolean
  durationDays: number
  accessToken: string
  errorText: string
}

export class IssueTokenDialog extends Component<IIssueTokenDialogProps, IIssueTokenDialogState> {
  public static open(props) {
    return OverlayManager.openOverlayElement(
      <Modal>
        <IssueTokenDialog {...props} />
      </Modal>
    )
  }

  constructor(props) {
    super(props)
    this.state = {
      isLoading: false,
      durationDays: DEFAULT_DURATION_DAYS,
      accessToken: null,
      errorText: null,
    }
  }

  public render() {
    const { onClose } = this.props
    const { isLoading, accessToken } = this.state

    const hasResult = !_.isNil(accessToken)
    const primaryText = hasResult ? 'Copy' : 'Issue Token'

    return (
      <div>
        <div className='c-modalHeader'>
          <h4 className='c-modal-title'>Issue API Token</h4>
        </div>
        <div className='c-modalBody'>
          {hasResult ? this.renderResult() : this.renderForm()}
        </div>
        <Footer
          isVisible={true}
          cancelButtonText='Close'
          isPrimaryButtonLoading={isLoading}
          primaryButtonText={primaryText}
          onCancelButtonClick={onClose}
          onPrimaryButtonClick={this.handlePrimaryClick}
        />
      </div>
    )
  }

  private renderForm() {
    const { durationDays, errorText } = this.state
    return (
      <>
        <InputField
          autoFocus={true}
          isHorizontalLayout={true}
          label='Duration (days)'
          type='number'
          onChange={(value) => this.setState({ durationDays: Math.max(1, parseInt(value, 10) || 1) })}
          value={durationDays}
        />
        {!_.isEmpty(errorText) && <ErrorBlock errorText={errorText} />}
      </>
    )
  }

  private renderResult() {
    const { accessToken, durationDays } = this.state
    const years = (durationDays / 365).toFixed(1)
    const expiryNote = durationDays >= 365
      ? `Expires in ~${years} year${parseFloat(years) !== 1 ? 's' : ''}`
      : `Expires in ${durationDays} day${durationDays !== 1 ? 's' : ''}`

    return (
      <>
        <InputField
          isHorizontalLayout={true}
          isReadOnly={true}
          label='Access Token'
          value={accessToken}
        />
        <InputField
          isHorizontalLayout={true}
          isReadOnly={true}
          isDisabled={true}
          label='Expiry'
          value={expiryNote}
        />
      </>
    )
  }

  private handlePrimaryClick = () => {
    const { accessToken } = this.state
    if (!_.isNil(accessToken)) {
      this.copyToClipboard(accessToken)
    } else {
      void this.issueToken()
    }
  }

  private async issueToken() {
    const { targetUserId } = this.props
    const { durationDays } = this.state

    this.setState({ isLoading: true, errorText: null })
    try {
      const response = await apis.issueToken(targetUserId, durationDays)
      this.setState({ isLoading: false, accessToken: response.accessToken })
    } catch (error) {
      this.setState({ isLoading: false, errorText: stringifyError(error) })
    }
  }

  private copyToClipboard(token: string) {
    navigator.clipboard.writeText(token)
    Toast.show({
      message: 'Copied token to clipboard!',
      position: Position.BOTTOM_RIGHT,
    })
  }
}
